import { MapPin, Phone, Clock, MessageCircle, Facebook, Instagram, Twitter } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const QUICK_LINKS = [
  { id: "services", label: "Services" },
  { id: "why-nidan", label: "Why Nidan" },
  { id: "how-it-works", label: "How It Works" },
  { id: "before-visit", label: "Before Your Visit" },
  { id: "appointment", label: "Book Appointment" },
  { id: "visit-us", label: "Contact" },
];

const SERVICE_LINKS = [
  "Ultrasound / Sonography",
  "Pregnancy Sonography",
  "3D / 4D Scan",
  "Colour Doppler",
  "Digital X-Ray",
  "Dental OPG X-Ray",
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-900 text-slate-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="bg-white rounded-2xl p-4 inline-block">
              <img
                src="/assets/logo-nidan.jpeg"
                alt="Nidan Diagnostic Centre logo"
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
              Nidan Diagnostic Centre is a trusted diagnostic imaging centre in Wagholi,
              Pune — offering sonography, ultrasound, 3D/4D scans, colour doppler, digital
              X-ray and dental OPG with experienced radiologist Dr. Pramod Waghole.
            </p>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-cta"
              className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold py-2.5 px-5 rounded-full transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Chat on WhatsApp
            </a>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-white tracking-wider uppercase">Quick Links</h4>
            <ul className="mt-4 space-y-2.5">
              {QUICK_LINKS.map((q) => (
                <li key={q.id}>
                  <a
                    href={`#${q.id}`}
                    data-testid={`footer-link-${q.id}`}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {q.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold text-white tracking-wider uppercase">Services</h4>
            <ul className="mt-4 space-y-2.5">
              {SERVICE_LINKS.map((s) => (
                <li key={s}>
                  <a
                    href="#services"
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold text-white tracking-wider uppercase">Contact</h4>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-[#00BFFF]" />
                <span>
                  Office No. 103, First Floor, Laxmi Plaza, Rudra Complex, Kesnand Road,
                  Wagholi, Pune
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#00BFFF]" />
                <a href="tel:+918854865454" className="hover:text-white">
                  8854865454
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 mt-0.5 text-[#00BFFF]" />
                <span>
                  Mon – Sat &middot; 10:00 AM – 9:00 PM
                  <br />
                  Sunday: by appointment
                </span>
              </li>
            </ul>

            <div className="mt-6 flex items-center gap-3">
              <a
                href="#"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#00BFFF] flex items-center justify-center transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#00BFFF] flex items-center justify-center transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="#"
                aria-label="Twitter"
                data-testid="social-twitter"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#00BFFF] flex items-center justify-center transition-colors"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Nidan Diagnostic Centre. All rights reserved.
          </p>
          <p className="text-xs text-slate-500" data-testid="footer-credit">
            Design and Developed by <span className="text-slate-300 font-semibold">SM Computers</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
